import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Observable } from 'rxjs';

import { map, tap } from 'rxjs/operators';

import { CsrfTokenService } from './csrf.service';
import { StorageResetService } from './storage-reset.service';
import {
  AuthenticationApiService,
  MagicLinkDtoApiModel,
  MagicLinkVerifyDtoApiModel,
  MagicLinkVerifyResponseDtoApiModel,
  PasswordSigninDtoApiModel,
  RequestPasswordResetDtoApiModel,
  ResetPasswordDtoApiModel,
  ResetPasswordResponseDtoApiModel,
  SigninResponseDtoApiModel,
  SignupDtoApiModel,
  TwoFactorCodeSentDtoApiModel,
  TwoFactorPendingResponseDtoApiModel,
  VerifyPasswordResetDtoApiModel,
  VerifyPasswordResetResponseDtoApiModel,
} from '../../api';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isAuthenticatedSignal: WritableSignal<boolean> = signal(false);
  public isAuthenticated$: Observable<boolean> = toObservable(
    this.isAuthenticatedSignal,
  );

  private readonly authenticationApiService: AuthenticationApiService = inject(
    AuthenticationApiService,
  );
  private readonly csrfTokenService: CsrfTokenService = inject(CsrfTokenService);

  private readonly storageResetService: StorageResetService =
    inject(StorageResetService);

  public sendMagicLink(email: MagicLinkDtoApiModel): Observable<void> {
    return this.authenticationApiService
      .authControllerSendMagicLink(email)
      .pipe(map(() => undefined));
  }

  public signup(credentials: SignupDtoApiModel): Observable<SigninResponseDtoApiModel> {
    return this.authenticationApiService
      .authControllerSignup(credentials)
      .pipe(tap(() => this.isAuthenticatedSignal.set(true)));
  }

  public magicLinkVerify(
    body: MagicLinkVerifyDtoApiModel,
  ): Observable<MagicLinkVerifyResponseDtoApiModel> {
    return this.authenticationApiService.authControllerMagicLinkVerify(body);
  }

  public passwordSignin(
    body: PasswordSigninDtoApiModel,
  ): Observable<SigninResponseDtoApiModel> {
    return this.authenticationApiService
      .authControllerPasswordSignin(body)
      .pipe(
        tap((signin) => {
          if (!signin.twoFactorRequired) {
            this.isAuthenticatedSignal.set(true);
          }
        }),
      );
  }

  public verifyTwoFactor(
    code: string,
    rememberDevice: boolean,
  ): Observable<SigninResponseDtoApiModel> {
    return this.authenticationApiService
      .authControllerVerifyTwoFactor({ code, rememberDevice })
      .pipe(tap(() => this.isAuthenticatedSignal.set(true)));
  }

  public recoverTwoFactor(code: string): Observable<SigninResponseDtoApiModel> {
    return this.authenticationApiService
      .authControllerRecoverTwoFactor({ code })
      .pipe(tap(() => this.isAuthenticatedSignal.set(true)));
  }

  public resendTwoFactor(): Observable<TwoFactorCodeSentDtoApiModel> {
    return this.authenticationApiService.authControllerResendTwoFactor();
  }

  public pendingTwoFactor(): Observable<TwoFactorPendingResponseDtoApiModel> {
    return this.authenticationApiService.authControllerPendingTwoFactor();
  }

  public requestPasswordReset(
    body: RequestPasswordResetDtoApiModel,
  ): Observable<void> {
    return this.authenticationApiService
      .authControllerRequestPasswordReset(body)
      .pipe(map(() => undefined));
  }

  public resetPassword(
    body: ResetPasswordDtoApiModel,
  ): Observable<ResetPasswordResponseDtoApiModel> {
    return this.authenticationApiService.authControllerResetPassword(body);
  }

  public verifyPasswordReset(
    body: VerifyPasswordResetDtoApiModel,
  ): Observable<VerifyPasswordResetResponseDtoApiModel> {
    return this.authenticationApiService.authControllerVerifyPasswordReset(
      body,
    );
  }

  public signout(): Observable<void> {
    return this.authenticationApiService.authControllerSignout().pipe(
      tap(() => {
        this.isAuthenticatedSignal.set(false);
        this.csrfTokenService.clearToken();
        this.storageResetService.clearUserScopedData();
      }),
      map(() => undefined),
    );
  }
}
